/**
 * `lazy()` con auto-recuperación ante chunks viejos ("stale chunk").
 *
 * Tras cada deploy los hashes de los chunks cambian. Una pestaña abierta con el bundle ANTERIOR
 * —o un service worker sirviendo un index viejo— pide un chunk que ya no existe: el import dinámico
 * falla con «Failed to fetch dynamically imported module» y TODA la vista cae al ErrorBoundary.
 * Es el clásico «andaba y de golpe no» después de publicar, sin un solo cambio en la página.
 *
 * Si el import falla, se pasa a la versión publicada con `actualizarAhora` —da de baja el service
 * worker, vacía las cachés y recarga— y eso se hace a lo sumo UNA vez por minuto. Si dentro de ese
 * minuto vuelve a fallar, se deja propagar el error real.
 *
 * 🚨 RECARGAR SOLO NO CURA (founder, 9-oct-2026, tras tres publicaciones seguidas): «se recarga
 *    sola la página cada un segundo» y el cartel «Error en Panel», en la laptop; en incógnito y en
 *    la PC, bien. El service worker guardaba trozos con `CacheFirst`, y Pages contesta un trozo que
 *    no existe con el index.html y un 200: la página quedó guardada COMO si fuera el trozo. Un F5
 *    vuelve a pedirle el trozo al service worker y recibe la misma página — por eso la cura vacía
 *    las cachés antes de recargar, igual que el botón «Actualizar» del aviso de versión nueva.
 *
 * 🚨 Y EL CANDADO CONTRA EL BUCLE NO PUEDE LEVANTARLO OTRO TROZO. Antes era una marca que se
 *    borraba «al primer import exitoso». Pero una pantalla carga varios trozos: el de la ruta
 *    entraba bien y borraba la marca, el envenenado fallaba y recargaba, y así cada segundo. La
 *    marca ahora es la HORA de la última recarga: ningún éxito ajeno la borra.
 *
 * 🚨 VIVE ACÁ Y NO EN `routes/lazyPages.ts` A PROPÓSITO. Mientras estuvo encerrada en el archivo
 * del router, las 227 páginas quedaron cubiertas y los `lazy()` sueltos dentro de los componentes
 * NO. El founder lo vio en producción: «por momentos sale ese error» entrando a Chats, porque la
 * burbuja de ubicación y el modal de cobertura cargaban su chunk de mapas con un `lazy()` crudo.
 * Una herramienta que resuelve un problema general no puede estar guardada en el archivo de un
 * consumidor: el próximo que la necesite no la va a encontrar.
 */
import { lazy, type ComponentType } from 'react';
import { actualizarAhora } from '@/lib/laVersionDelPanel';

const MARCA = 'chunk-reload-at';

/** Entre dos recargas automáticas, al menos esto. Menos que un deploy, más que cualquier bucle. */
export const ENTRE_RECARGAS_MS = 60 * 1000;

/** Puro: ¿toca recargar, si la última recarga automática fue en `ultima` (ms; 0 = nunca)? */
export function tocaRecargar(ultima: number, ahora: number): boolean {
  return !(ultima > 0 && ahora - ultima >= 0 && ahora - ultima < ENTRE_RECARGAS_MS);
}

function leerMarca(): number {
  try { return Number(sessionStorage.getItem(MARCA)) || 0; } catch { return 0; }
}

function ponerMarca(ahora: number): void {
  try { sessionStorage.setItem(MARCA, String(ahora)); } catch { /* sin almacenamiento: igual se recarga */ }
}

/** La carga con su red: lo que `lazyWithReload` le da a `lazy()`. Exportada para probarla sin React. */
export async function cargarConRed<M>(factory: () => Promise<M>): Promise<M> {
  try {
    return await factory();
  } catch (err) {
    const ahora = Date.now();
    if (typeof window !== 'undefined' && tocaRecargar(leerMarca(), ahora)) {
      ponerMarca(ahora);
      void actualizarAhora();
      return new Promise<M>(() => {}); // cuelga hasta que la recarga tome efecto
    }
    throw err;
  }
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any -- mismo tipo que React.lazy para ser drop-in
export function lazyWithReload<T extends ComponentType<any>>(
  factory: () => Promise<{ default: T }>,
) {
  return lazy(() => cargarConRed(factory));
}
